import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { t } from '@fetmode/i18n';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { OnboardingDock } from './OnboardingDock.js';
import { scrollOnboardingToTop, useOnboardingScrollTop } from './onboarding-scroll.js';
import { TurnOnsPicker } from './TurnOnsPicker.js';
import { EMPTY_TURN_ONS_SELECTION, type TurnOnsSelection } from './turn-ons-catalog.js';
import './OnboardingFlow.css';

export function TurnOnsOnboardingPage(): ReactElement {
  const { api, setUser } = useAuth();
  const navigate = useNavigate();
  useOnboardingScrollTop();
  const [selection, setSelection] = useState<TurnOnsSelection>(EMPTY_TURN_ONS_SELECTION);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getTurnOns()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setSelection({
          genders: result.turnOns.genders,
          ageGroups: result.turnOns.ageGroups,
          ethnicities: result.turnOns.ethnicities,
          bodyTypes: result.turnOns.bodyTypes,
        });
      })
      .catch(() => {
        // Prefill is best-effort; empty form still works.
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const finish = async (next: TurnOnsSelection): Promise<void> => {
    setIsSaving(true);
    setError(null);

    try {
      await api.updateTurnOns({
        ...next,
        completed: true,
      });

      const updatedUser = await api.getMe();
      setUser(updatedUser);

      scrollOnboardingToTop();
      void navigate('/onboarding/tags', { replace: true });
    } catch {
      setError(t('common.turnOns.error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="onboarding-flow">
      <p className="onboarding-flow__eyebrow">{t('common.turnOns.eyebrow')}</p>
      <h1 className="onboarding-flow__title">{t('common.turnOns.title')}</h1>
      <p className="onboarding-flow__subtitle">{t('common.turnOns.subtitle')}</p>

      <TurnOnsPicker
        value={selection}
        onChange={setSelection}
        disabled={isSaving}
        omitCategories={['genders']}
      />

      {error ? <p className="fm-error">{error}</p> : null}

      <OnboardingDock
        selectedCount={
          selection.ageGroups.length + selection.ethnicities.length + selection.bodyTypes.length
        }
        continueLabel={isSaving ? t('common.turnOns.saving') : t('common.turnOns.continue')}
        continueDisabled={isSaving}
        skipDisabled={isSaving}
        onContinue={() => {
          void finish(selection);
        }}
        onSkip={() => {
          void finish({
            ...EMPTY_TURN_ONS_SELECTION,
            genders: selection.genders,
          });
        }}
      />
    </section>
  );
}
